import React from 'react';
import _ from 'lodash';
import { t } from 'i18next';
import { useSelector, useDispatch } from 'react-redux';
import { BrowserRouter as Router, Redirect, Route, Switch } from 'react-router-dom';
import { RoleService, AuthService } from 'Services';
import {
    getUser,
    shouldShowGlobalRules,
    shouldShowLists,
    shouldShowManageTenants,
    shouldShowMerchants,
    shouldShowParameters,
    shouldShowReports,
    shouldShowRevisions,
    shouldShowRuleTests,
    shouldShowSimulations
} from 'Selectors';
import { pushAlert } from 'Store';

import ForbiddenPage from 'Routes/ForbiddenPage';

import useTemplateConfig from 'Hooks/query-hooks/useTemplateConfig';
import useUserConfig from 'Hooks/query-hooks/useUserConfig';
import useGetUsers from 'Hooks/query-hooks/useGetUsers';
import useGetConclusionFlags from 'Hooks/query-hooks/useGetConclusionFlags';
import { ADDTIONAL_CONCLUSION_VALUES_KEY, useSearchParams } from 'Utils';

import RouteLoading from 'Components/RouteLoading';
import NotificationFactory from 'Components/NotificationFactory';
import ImportExportSidePanel from 'Components/SidePanel/ImportExportSidePanel';
import { getFlatRoutesArray } from 'Components/Breadcrumbs';
import NotFound from 'Components/ErrorScreens/NotFound';
import errorController from 'Components/ErrorScreens';
import { ROUTES } from 'Components/Breadcrumbs/routes';

import PageElement from '@brighterion/templates/PageElement';
import RootElement from '@brighterion/templates/RootElement';
import type { AuthConfig } from '@brighterion/types/config';
import BackgroundProcessContainer from './BackgroundProcessContainer';
/**
 * @note import Normalize.css first before importing other styles
 */
import 'normalize.css';
import '@brighterion/br-ui/dist/theme/theme.css';
import '@brighterion/br-ui/dist/index.css';

import styles from './Routes.css';

const TemplateConfigAndUsers: React.FC = ({ children }) => {
    const templateConfig = useTemplateConfig();
    const users = useGetUsers();
    /**
     * Please use React-Query instead:
     * @example
     * const queryClient = useQueryClient();
     * const templateConfig = queryClient.getQueryData<TemplateConfig>([RQ_TEMPLATE_CONFIG]);
     */
    if (templateConfig.isLoading || templateConfig.isFetching || users.isLoading) {
        return <RouteLoading />;
    }

    if (templateConfig.isError || users.isError) {
        const { errorCmp } = errorController([templateConfig.error, users.error]);
        return errorCmp;
    }

    if (!users.data || !templateConfig.data) {
        return <RouteLoading />;
    }

    return <>{children}</>;
};

type AppProps<T = any> = {
    authConfig: T;
    uniqueUserTokenId: string;
};

const Routes: React.FC<AppProps<AuthConfig>> = ({ authConfig, uniqueUserTokenId }) => {
    const { flow, name } = useSearchParams();
    const { landlordId, tenantKey } = useSelector(getUser);
    const { hasAnyRulesRole } = RoleService;
    const dispatch = useDispatch();
    /**
     * Get the user config and set query key based on the unique User Token Id.
     * The unique User Token Id is required for impersonating other users.
     * Switching back will return the cached `User Config` from React Query.
     */
    const userConfig = useUserConfig(uniqueUserTokenId);
    const conclusionFlagList = useGetConclusionFlags();

    // LOGOUT for MC Connect
    React.useEffect(() => {
        // https://auth-broker.brighterion.io/auth-broker/oauth
        window.addEventListener('connectsignout', (event: Event) => {
            event.preventDefault();
            AuthService.logout().then(() => {
                event.detail.resume();
            });
        });
        // TODO not sure if this works
        window.addEventListener('connectsignoutcomplete', () => {
            AuthService.logout();
        });
        return () => {
            window.removeEventListener('connectsignout', () => {});
            window.removeEventListener('connectsignoutcomplete', () => {});
        };
    }, []);

    React.useEffect(() => {
        if (flow === 'switch') {
            dispatch(
                pushAlert({
                    id: 'change-ruleSet',
                    content: t('RuleSetSwitchedBannerText', { displayName: name }),
                    status: 'success'
                })
            );
        }
    }, [dispatch, flow, name]);

    if (userConfig.isLoading || userConfig.isRefetching) return <RouteLoading />;

    if (userConfig.isError) {
        const { errorCmp } = errorController([userConfig.error]);
        return <div className={styles.error}>{errorCmp}</div>;
    }

    if (!conclusionFlagList.isError && conclusionFlagList.isFetched && conclusionFlagList.data) {
        localStorage.setItem(ADDTIONAL_CONCLUSION_VALUES_KEY, JSON.stringify(conclusionFlagList.data));
    }

    const isMastercardConnectUser = typeof authConfig?.idp === 'undefined' || authConfig?.idp === 'mcconnect';

    if (hasAnyRulesRole) {
        return (
            <TemplateConfigAndUsers>
                <Router>
                    <RootElement options={{ isMastercardConnectUser }}>
                        <NotificationFactory />
                        <ImportExportSidePanel />
                        <Switch>
                            <Route
                                exact
                                path='/'
                                render={() => {
                                    /** Flows can be used for redirection after the user is authenticated */
                                    if (flow === 'tenants') {
                                        return (
                                            <Redirect to={{ pathname: ROUTES.TENANTS.INDEX, search: '?tab=tenants' }} />
                                        );
                                    }

                                    if (RoleService.hasDesignAccess && shouldShowGlobalRules()) {
                                        return <Redirect to={ROUTES.GLOBAL_RULES.INDEX} />;
                                    }

                                    if (
                                        RoleService.hasAnyTenantRole &&
                                        shouldShowManageTenants(landlordId, tenantKey)
                                    ) {
                                        return <Redirect to={ROUTES.TENANTS.INDEX} />;
                                    }

                                    if (RoleService.hasParameterAccess && shouldShowParameters()) {
                                        return <Redirect to={ROUTES.PARAMETERS.INDEX} />;
                                    }

                                    if (RoleService.hasDesignAccess && shouldShowMerchants()) {
                                        return <Redirect to={ROUTES.OVERRIDES.INDEX} />;
                                    }

                                    if (RoleService.hasListOrDesignAccess && shouldShowLists()) {
                                        return <Redirect to={ROUTES.LIST_MANAGEMENT.INDEX} />;
                                    }

                                    if (RoleService.hasDesignAccess && shouldShowSimulations()) {
                                        return <Redirect to={ROUTES.SIMULATIONS.INDEX} />;
                                    }

                                    if (shouldShowRuleTests()) {
                                        return <Redirect to={ROUTES.RULE_TESTS.INDEX} />;
                                    }

                                    if (RoleService.hasReportAccess && shouldShowReports()) {
                                        return <Redirect to={ROUTES.REPORTS} />;
                                    }

                                    if (RoleService.hasDesignAccess && shouldShowRevisions()) {
                                        return <Redirect to={ROUTES.REVISIONS} />;
                                    }

                                    return <Redirect to='/403' />;
                                }}
                            />
                            {getFlatRoutesArray(landlordId, tenantKey).map(
                                ({ component: Component, isForbidden, showFeature, path }) => {
                                    return (
                                        <Route
                                            key={_.uniqueId('route_')}
                                            exact
                                            path={path}
                                            render={(props) => {
                                                /** Redirect user if the route is forbidden or feature flag is off */
                                                if (isForbidden || !showFeature) return <Redirect to='/403' />;
                                                return (
                                                    Component && (
                                                        <PageElement head={{ title: props.location.pathname }}>
                                                            <Component {...props} />
                                                        </PageElement>
                                                    )
                                                );
                                            }}
                                        />
                                    );
                                }
                            )}

                            <Route key='forbiddenPage' path='/403'>
                                <PageElement head={{ title: '403 Forbidden' }}>
                                    <ForbiddenPage />
                                </PageElement>
                            </Route>

                            <Route key='notFoundPage' path='*'>
                                <PageElement head={{ title: '404 Page Not Found' }}>
                                    <NotFound />
                                </PageElement>
                            </Route>
                        </Switch>
                        <BackgroundProcessContainer />
                    </RootElement>
                </Router>
            </TemplateConfigAndUsers>
        );
    }

    return <ForbiddenPage />;
};

export default Routes;
